import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5timeline from '@amcharts/amcharts5/timeline';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { Subscription } from 'rxjs';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Road Timeline Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-road-timeline-graph',
  standalone: false,
  templateUrl: './road-timeline-graph.component.html',
  styleUrl: './road-timeline-graph.component.scss'
})
export class RoadTimelineGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private roadTimelineGraphReportSubscription: any;
  private isRoadTimelineViewInitialized = false;

  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.roadTimelineGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Road Timeline Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isRoadTimelineViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isRoadTimelineViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isRoadTimelineViewInitialized) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  private createChart(items: any[]): void {
    this.root = am5.Root.new("road-timeline");
    this._baseChartService.attachExporting(this.root, { 
      filePrefix: 'road-timeline',
      data: items
    });
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    const chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        layout: this.root.verticalLayout,
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "none"
      })
    );

    const timelineData = this.transformDataForRoadTimeline(items);

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(this.root, {
        baseInterval: { timeUnit: "day", count: 1 },
        renderer: am5xy.AxisRendererX.new(this.root, { minGridDistance: 60 }),
        tooltip: am5.Tooltip.new(this.root, {})
      })
    );

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        min: 0,
        max: 100,
        renderer: am5xy.AxisRendererY.new(this.root, { visible: false })
      })
    );

    // Create elegant curved road path
    const roadSeries = chart.series.push(
      am5xy.LineSeries.new(this.root, {
        xAxis, yAxis,
        valueYField: "roadY",
        valueXField: "roadX",
        stroke: am5.color(0x2c3e50),
        connect: true
      })
    );
    
    // Configure road with gradient and shadow
    roadSeries.strokes.template.setAll({ 
      strokeWidth: 16,
      strokeOpacity: 0.8
    });

    // Add road shadow
    const roadShadow = chart.series.push(
      am5xy.LineSeries.new(this.root, {
        xAxis, yAxis,
        valueYField: "roadY",
        valueXField: "roadX",
        stroke: am5.color(0x000000),
        connect: true
      })
    );
    roadShadow.strokes.template.setAll({ 
      strokeWidth: 16,
      strokeOpacity: 0.2
    });

    // Milestones with elegant design
    const milestoneSeries = chart.series.push(
      am5xy.LineSeries.new(this.root, { 
        xAxis, yAxis, 
        valueYField: "milestoneY", 
        valueXField: "milestoneX", 
        connect: false 
      })
    );
    milestoneSeries.strokes.template.setAll({ strokeWidth: 0 });
    
    // Configure tooltip for milestones
    const tooltip = am5.Tooltip.new(this.root, {
      labelText: "{task}\nValue: {value}\nStart: {start}\nEnd: {end}",
      pointerOrientation: "horizontal"
    });
    milestoneSeries.set("tooltip", tooltip);
    
    // Create elegant milestone bullets
    milestoneSeries.bullets.push((_root, _series, dataItem) => {
      const circle = am5.Circle.new(this.root, {
        radius: 12,
        fill: (dataItem.dataContext as any)?.color || am5.color(0x3498db),
        stroke: am5.color(0xffffff),
        strokeWidth: 3,
        shadowColor: am5.color(0x000000),
        shadowBlur: 5,
        shadowOffsetX: 2,
        shadowOffsetY: 2
      });

      return am5.Bullet.new(this.root, {
        locationX: 0.5,
        sprite: circle
      });
    });

    // Labels with elegant styling
    const labelSeries = chart.series.push(
      am5xy.LineSeries.new(this.root, { 
        xAxis, yAxis, 
        valueYField: "labelY", 
        valueXField: "labelX", 
        connect: false 
      })
    );
    labelSeries.strokes.template.setAll({ strokeWidth: 0 });
    
    labelSeries.bullets.push((root, _series, dataItem) => {
      const label = am5.Label.new(this.root, {
        text: (dataItem.dataContext as any)?.task || "",
        fontSize: 14,
        fill: am5.color(0x2c3e50),
        centerX: am5.p50,
        centerY: am5.p50
      });

      // Add label background
      const background = am5.Rectangle.new(this.root, {
        fill: am5.color(0xffffff),
        fillOpacity: 0.9,
        stroke: am5.color(0xe0e0e0),
        strokeWidth: 1
      });

      const container = am5.Container.new(this.root, {});
      container.children.push(background);
      container.children.push(label);

      return am5.Bullet.new(this.root, {
        locationX: 0.5,
        sprite: container
      });
    });

    // Set data
    roadSeries.data.setAll(this.createElegantRoadPathData(timelineData));
    roadShadow.data.setAll(this.createElegantRoadPathData(timelineData));
    milestoneSeries.data.setAll(this.createElegantMilestoneData(timelineData));
    labelSeries.data.setAll(this.createElegantLabelData(timelineData));

    chart.set("cursor", am5xy.XYCursor.new(this.root, { behavior: "zoomX", xAxis }));

    const legend = chart.children.push(
      this._baseChartService.createLegend(this.root)
    );
    legend.setAll({
      width: am5.percent(100),
      centerX: am5.p50,
      x: am5.p50,
      marginTop: 8,
      marginBottom: 8
    });
    legend.markers.template.setAll({ width: 12, height: 12 });
    legend.labels.template.setAll({
      text: '{task}',
      fontSize: 12,
      oversizedBehavior: 'truncate',
      maxWidth: 180
    });
    legend.valueLabels.template.set('forceHidden', true);
    legend.markerRectangles.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      return (dataItem?.dataContext as any)?.color ?? fill;
    });
    legend.markerRectangles.template.adapters.add('stroke', (stroke, target) => {
      const dataItem = target.dataItem;
      return (dataItem?.dataContext as any)?.color ?? stroke;
    });
    const updateLegend = () => {
      if (milestoneSeries.dataItems?.length > 0) {
        legend.data.setAll(milestoneSeries.dataItems);
      }
    };
    milestoneSeries.events.on('datavalidated', updateLegend);
    updateLegend();
    this._baseChartService.setupLegendAnimation(legend);

    chart.appear(1000, 100);
  }

  private createElegantRoadPathData(data: any[]): any[] {
    if (!data.length) return [];

    const start = data[0].start;
    const end = data[data.length - 1].end;
    const total = end - start;
    
    // Create smooth curve with more points
    return Array.from({ length: 50 }, (_, i) => {
      const p = i / 49;
      // Create a more elegant curve using cubic function
      const curve = Math.sin(p * Math.PI * 1.5) * 0.3 + Math.sin(p * Math.PI * 0.5) * 0.2;
      return { 
        roadX: start + total * p, 
        roadY: 50 + curve * 30 
      };
    });
  }

  private createElegantMilestoneData(data: any[]): any[] {
    const len = Math.max(data.length - 1, 1);
    return data.map((d, i) => {
      const p = i / len;
      const curve = Math.sin(p * Math.PI * 1.5) * 0.3 + Math.sin(p * Math.PI * 0.5) * 0.2;
      return {
        milestoneX: d.start,
        milestoneY: 50 + curve * 30,
        task: d.task,
        value: d.value,
        start: new Date(d.start).toLocaleDateString(),
        end: new Date(d.end).toLocaleDateString(),
        color: d.color
      };
    });
  }

  private createElegantLabelData(data: any[]): any[] {
    const len = Math.max(data.length - 1, 1);
    return data.map((d, i) => {
      const p = i / len;
      const curve = Math.sin(p * Math.PI * 1.5) * 0.3 + Math.sin(p * Math.PI * 0.5) * 0.2;
      return {
        labelX: d.start,
        labelY: 50 + curve * 30 + 35, // Position above the road
        task: d.task
      };
    });
  }

  private transformDataForRoadTimeline(_data_: BarChartData[]): any[] {
    const timelineData: any[] = [];
    console.log('Transforming data for road timeline:', _data_);
 
    if (_data_.length === 0) {
      console.log('No data provided');
      return timelineData;
    }
 
    // Get the first item to determine field names dynamically_
    const firstItem = _data_[0];
    const keys = Object.keys(firstItem);
    console.log('Available fields:', keys);
 
    if (keys.length < 2) {
      console.log('Not enough fields in data');
      return timelineData;
    }
 
  // Assume first field is category/label, second field is value_
    const categoryField = keys[0];
    const valueField = keys[1];
   
    console.log('Using fields - Category:', categoryField, 'Value:', valueField);
 
    // Use the original data to create timeline entries_
    _data_.forEach((_item_: BarChartData, _index_: number) => {
      console.log('Processing item for road timeline:', _item_);
     
      // Get category and value dynamically_
      const category = _item_[categoryField];
      const value = _item_[valueField];
     
      // Skip empty categories or zero/null values_
      if (!category || category === '' || value === 0 || value === null || value === undefined) {
        console.log('Skipping item with empty category or invalid value:', _item_);
        return;
      }
     
      if (value !== undefined && value !== null) {
        // Create timeline entry with start and end dates_
        const baseDate = new Date();
        const start = new Date(baseDate);
        start.setDate(start.getDate() + _index_ * 5); // Each item starts 5 days apart_
 
        const end = new Date(start);
        const numericValue = typeof value === 'number' ? Math.max(1, Math.min(value, 30)) : 7; // Limit to 1-30 days_
        end.setDate(end.getDate() + numericValue);
 
        const timelineItem = {
          category: "Road",
          start: start.getTime(),
          end: end.getTime(),
          task: category,
          value: value, // Add the original value for tooltip
          roadY: 50, // Road segments at Y=50_
          color: am5.ColorSet.new(this.root, {}).getIndex(_index_)
        };
       
        timelineData.push(timelineItem);
        console.log('Created road timeline item:', timelineItem);
      }
    });
 
    // If no data was processed, create some sample data to show the chart structure_
    if (timelineData.length === 0) {
      console.log('No valid data found for road timeline, creating sample data');
      const currentDate = new Date();
      const sampleCategories = ['Planning', 'Design', 'Development', 'Testing', 'Deployment'];
 
      sampleCategories.forEach((_category_, _index_) => {
        const start = new Date(currentDate);
        start.setDate(start.getDate() + _index_ * 5);
       
        const end = new Date(start);
        end.setDate(end.getDate() + 7);
 
        timelineData.push({
          category: "Road",
          start: start.getTime(),
          end: end.getTime(),
          task: _category_,
          value: 7, // Sample value for tooltip
          roadY: 50,
          color: am5.ColorSet.new(this.root, {}).getIndex(_index_)
        });
      });
    }
 
    console.log('Final road timeline data:', timelineData);
    return timelineData;
  }

  private updateChart(data: BarChartData[]) {
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
    }
    this.createChart(data);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  

  private createLabelData(timelineData: any[]): any[] {
    const labelData: any[] = [];
    
    timelineData.forEach((item, index) => {
      const progress = index / (timelineData.length - 1);
      const y = 50 + Math.sin(progress * Math.PI * 2) * 10 + 20; // Above the road
      
      labelData.push({
        labelX: item.start,
        labelY: y,
        task: item.task
      });
    });

    return labelData;
  }

  

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.roadTimelineGraphReportSubscription) {
      this.roadTimelineGraphReportSubscription.unsubscribe();
    }
  }
}
